import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import {
  DocumentMiscIcon,
} from '../assets/documentIcons';

import LoadingSpinner from './LoadingSpinner';
import DatasetDetailSchemaTable from './DatasetDetailSchemaTable';
import { setLoading } from '../reducers/loadingReducer';
import { fetchDataset } from '../services/apiService';
import { setSelectedDataset } from '../reducers/datasetsReducer';
import { setSelectedPromptItem } from '../reducers/uiReducer';

const getFileIcon = (fileType) => {
  return <DocumentMiscIcon />;
};

const DatasetDetail = ({ selectedDataset }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width: 800px)');
  const [showConfirmDelete] = useState(false);
  const promptItems = useSelector((state) => state.ui.promptItems);
  const isLoading = useSelector((state) => state.loading);

  const fetchUserDataset = async (datasetId) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchDataset(datasetId);
      dispatch(setSelectedDataset(response));
    } catch (error) {
      console.error('Error fetching dataset:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const checkAndUpdatePromptItem = (selectedDataset, promptItems) => {
    if (!selectedDataset || !promptItems) return;

    const matchingItem = promptItems.find((item) => item?.id === selectedDataset.id);
    if (matchingItem) {
      dispatch(setSelectedPromptItem(matchingItem));
    }
  };

  useEffect(() => {
    checkAndUpdatePromptItem(selectedDataset, promptItems);
    if (selectedDataset?.id) {
      fetchUserDataset(selectedDataset.id);
    }
  }, []);

  return (
    <Box
      sx={{
        height: 310,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        p: 2,
        borderRadius: '6px',
        overflowY: 'auto',
        opacity: 0,
        animation: 'fadeIn 0.4s forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <LoadingSpinner size={30} />
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      ) : selectedDataset ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              width: '100%',
            }}
          >
            <Box>{getFileIcon(selectedDataset.fileType)}</Box>
            <Box>
              <Typography variant="h6">{selectedDataset.dataSetName}</Typography>
              <Typography variant="body2">{`Source Type: ${selectedDataset.dataSourceConfig.sourceType}`}</Typography>
              <Typography variant="body2">{`Record Count: ${selectedDataset.dataSourceConfig.sourceRecordSize}`}</Typography>
              <Typography variant="body2">
                {selectedDataset.dataSourceConfig.isProcessed ? 'Processed' : 'Unprocessed'}
              </Typography>
            </Box>
          </Box>

          {selectedDataset.schema && (
            <DatasetDetailSchemaTable schema={selectedDataset.schema} />
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default DatasetDetail;
